/**
 * PipelineControlPanel.ts
 *
 * Control panel for pipeline processing with tabs, save/load, and pass management.
 * Works with any pipeline that implements the required interface.
 */

export interface PipelineTabConfig {
  id: string;
  label: string;
  pipeline: PipelineInterface;
  /** Optional set of parameter names that represent distances and should be shown in cm */
  distanceParameters?: Set<string>;
}

export interface PipelineInterface {
  getPassNames(): string[];
  isPassEnabled(name: string): boolean;
  enablePass(name: string): void;
  disablePass(name: string): void;
  getPass(index: number): PassInterface | undefined;
  movePass(fromIndex: number, toIndex: number): boolean;
  removePass?(index: number): boolean;
  passTimings?: Map<string, number>;
  exportToJSON(): string;
  loadFromJSON(json: string): boolean;
  processorRegistry?: Map<string, { defaultKwargs?: Record<string, unknown> }>;
  getDefaultKwargsForProcessor?(processorId: string): Record<string, unknown> | undefined;
  addPassFromRegistry?(processorId: string, kwargs?: Record<string, unknown>): boolean;
  loadedFromFilename?: string | null;
}

export interface PassInterface {
  name: string;
  kwargs?: Record<string, unknown>;
}

export interface PipelineControlPanelCallbacks {
  onPassToggled?: (passName: string, enabled: boolean) => void;
  onAllPassesToggled?: (enabled: boolean) => void;
  onPassMoved?: (fromIndex: number, toIndex: number) => void;
  onPassDeleted?: (index: number) => void;
  onPassParameterChanged?: (index: number, key: string, value: unknown) => void;
  onPipelineChanged?: (tabId: string) => void; // Called after save/load/add/toggle/move/delete/param change - includes which pipeline changed
  onTabSwitched?: (tabId: string) => void;
}

export class PipelineControlPanel {
  private container: HTMLDivElement;
  private tabsContainer: HTMLDivElement;
  private buttonContainer: HTMLDivElement;
  private globalToggleContainer: HTMLDivElement;
  private passesContainer: HTMLDivElement;

  private tabs: Map<string, PipelineTabConfig> = new Map();
  private activeTabId: string | null = null;
  private tabButtons: Map<string, HTMLButtonElement> = new Map();

  private callbacks: PipelineControlPanelCallbacks;
  private passCheckboxes: Map<string, HTMLInputElement> = new Map();
  private timingLabels: Map<string, HTMLElement> = new Map();
  private expandedPasses: Set<string> = new Set();
  private pixelsToCm: ((pixels: number) => number) | null = null;
  private cmToPixels: ((cm: number) => number) | null = null;

  constructor(callbacks: PipelineControlPanelCallbacks = {}) {
    this.callbacks = callbacks;
    this.container = document.createElement('div');

    // Pipeline tabs container
    this.tabsContainer = document.createElement('div');
    this.tabsContainer.style.display = 'flex';
    this.tabsContainer.style.marginBottom = '10px';
    this.tabsContainer.style.gap = '5px';
    this.container.appendChild(this.tabsContainer);

    // Button container
    this.buttonContainer = this.createButtonContainer();
    this.container.appendChild(this.buttonContainer);

    // Global toggle
    this.globalToggleContainer = this.createGlobalToggle();
    this.container.appendChild(this.globalToggleContainer);

    // Passes container
    this.passesContainer = document.createElement('div');
    this.passesContainer.id = 'passes-container';
    this.container.appendChild(this.passesContainer);
  }

  /**
   * Get the panel container element
   */
  getElement(): HTMLDivElement {
    return this.container;
  }

  /**
   * Set converters for displaying distance parameters in cm
   * @param pixelsToCm Function to convert pixel values to cm for display
   * @param cmToPixels Function to convert cm values back to pixels for storage
   */
  setUnitConverters(pixelsToCm: (pixels: number) => number, cmToPixels: (cm: number) => number): void {
    this.pixelsToCm = pixelsToCm;
    this.cmToPixels = cmToPixels;
    // Refresh the UI to show updated units
    if (this.activeTabId) {
      this.refreshPassesList();
    }
  }

  /**
   * Add a pipeline tab
   */
  addTab(config: PipelineTabConfig): void {
    this.tabs.set(config.id, config);

    const button = document.createElement('button');
    button.textContent = config.label;
    button.style.flex = '1';
    button.style.padding = '8px';
    button.style.cursor = 'pointer';
    button.style.background = 'rgba(255, 255, 255, 0.1)';
    button.style.border = 'none';
    button.style.color = 'white';
    button.style.borderRadius = '3px';
    button.onclick = () => this.switchTab(config.id);

    this.tabButtons.set(config.id, button);
    this.tabsContainer.appendChild(button);

    // If first tab, make it active
    if (this.tabs.size === 1) {
      this.switchTab(config.id);
    }
  }

  /**
   * Switch to a different tab
   */
  switchTab(tabId: string): void {
    const config = this.tabs.get(tabId);
    if (!config) return;

    this.activeTabId = tabId;

    // Update tab button styles
    this.tabButtons.forEach((button, id) => {
      if (id === tabId) {
        button.style.background = '#522e78';
        button.style.fontWeight = 'bold';
      } else {
        button.style.background = 'rgba(255, 255, 255, 0.1)';
        button.style.fontWeight = 'normal';
      }
    });

    // Refresh passes list
    this.refreshPassesList();

    // Call callback
    this.callbacks.onTabSwitched?.(tabId);
  }

  /**
   * Get the currently active pipeline
   */
  getActivePipeline(): PipelineInterface | null {
    if (!this.activeTabId) return null;
    const config = this.tabs.get(this.activeTabId);
    return config?.pipeline || null;
  }

  /**
   * Get the active tab ID
   */
  getActiveTabId(): string | null {
    return this.activeTabId;
  }

  /**
   * Get the currently active tab config
   */
  getActiveTabConfig(): PipelineTabConfig | null {
    if (!this.activeTabId) return null;
    return this.tabs.get(this.activeTabId) || null;
  }

  /**
   * Refresh the passes list for the active pipeline
   */
  /**
   * Update only the per-pass timing labels in place. Safe to call on a
   * fast interval — unlike refreshPassesList it does NOT rebuild the DOM,
   * so checkboxes/inputs stay interactive while timings tick.
   */
  refreshTimings(): void {
    const tabConfig = this.getActiveTabConfig();
    if (!tabConfig) return;
    for (const [passName, label] of this.timingLabels) {
      const timing = tabConfig.pipeline.passTimings?.get(passName);
      label.textContent = timing !== undefined ? `${timing.toFixed(1)}ms` : '';
    }
  }

  refreshPassesList(): void {
    this.passesContainer.innerHTML = '';
    this.passCheckboxes.clear();
    this.timingLabels.clear();

    const tabConfig = this.getActiveTabConfig();
    if (!tabConfig) {
      this.passesContainer.textContent = 'No pipeline selected';
      return;
    }

    const pipeline = tabConfig.pipeline;
    const passNames = pipeline.getPassNames();

    passNames.forEach((passName, index) => {
      const passElement = this.createPassElement(passName, index, pipeline, tabConfig.distanceParameters);
      this.passesContainer.appendChild(passElement);
    });
  }

  /**
   * Create a pass UI element
   */
  private createPassElement(passName: string, index: number, pipeline: PipelineInterface, distanceParameters?: Set<string>): HTMLDivElement {
    const passContainer = document.createElement('div');
    passContainer.style.marginBottom = '6px';
    passContainer.dataset.index = String(index);

    const passItem = document.createElement('div');
    passItem.draggable = true;
    passItem.style.display = 'flex';
    passItem.style.alignItems = 'center';
    passItem.style.padding = '5px';
    passItem.style.background = 'rgba(255, 255, 255, 0.05)';
    passItem.style.borderRadius = '3px';
    passItem.style.cursor = 'move';
    passItem.style.userSelect = 'none';
    passItem.style.border = '1px solid transparent';

    // Drag handle
    const dragHandle = document.createElement('span');
    dragHandle.textContent = '☰';
    dragHandle.style.marginRight = '8px';
    dragHandle.style.cursor = 'grab';
    dragHandle.style.color = '#888';
    passItem.appendChild(dragHandle);

    // Checkbox
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = pipeline.isPassEnabled(passName);
    checkbox.style.marginRight = '8px';
    checkbox.addEventListener('change', () => {
      // Handle toggle internally
      if (checkbox.checked) {
        pipeline.enablePass(passName);
      } else {
        pipeline.disablePass(passName);
      }
      // Notify callbacks
      this.callbacks.onPassToggled?.(passName, checkbox.checked);
      this.callbacks.onPipelineChanged?.(this.activeTabId || '');
    });
    passItem.appendChild(checkbox);
    this.passCheckboxes.set(passName, checkbox);

    // Pass name (clickable to expand)
    const nameSpan = document.createElement('span');
    nameSpan.textContent = passName;
    nameSpan.style.flex = '1';
    nameSpan.style.cursor = 'pointer';
    passItem.appendChild(nameSpan);

    // Timing display
    const timingSpan = document.createElement('span');
    timingSpan.style.marginRight = '8px';
    timingSpan.style.marginLeft = '12px';
    timingSpan.style.fontSize = '10px';
    timingSpan.style.color = '#888';
    timingSpan.style.fontFamily = 'monospace';
    const timing = pipeline.passTimings?.get(passName);
    if (timing !== undefined) {
      timingSpan.textContent = `${timing.toFixed(1)}ms`;
    }
    passItem.appendChild(timingSpan);
    this.timingLabels.set(passName, timingSpan);

    // Expand/collapse indicator
    const expandBtn = document.createElement('span');
    expandBtn.textContent = '▶';
    expandBtn.style.marginRight = '8px';
    expandBtn.style.cursor = 'pointer';
    expandBtn.style.fontSize = '10px';
    expandBtn.style.transition = 'transform 0.2s';
    passItem.appendChild(expandBtn);

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.textContent = '🗑️';
    deleteBtn.style.padding = '2px 6px';
    deleteBtn.style.cursor = 'pointer';
    deleteBtn.style.background = '#f44336';
    deleteBtn.style.border = 'none';
    deleteBtn.style.color = 'white';
    deleteBtn.style.borderRadius = '3px';
    deleteBtn.style.fontSize = '12px';
    deleteBtn.onclick = (e) => {
      e.stopPropagation();
      // Handle deletion internally
      if (pipeline.removePass) {
        pipeline.removePass(index);
        this.refreshPassesList();
      }
      // Notify callbacks
      this.callbacks.onPassDeleted?.(index);
      this.callbacks.onPipelineChanged?.(this.activeTabId || '');
    };
    passItem.appendChild(deleteBtn);

    // Parameters panel
    const paramsPanel = this.createParametersPanel(index, pipeline, distanceParameters);

    // Check if this pass was previously expanded
    const isExpanded = this.expandedPasses.has(passName);
    paramsPanel.style.display = isExpanded ? 'block' : 'none';
    expandBtn.style.transform = isExpanded ? 'rotate(90deg)' : 'rotate(0deg)';

    // Toggle expand/collapse
    const toggleExpand = (e: Event) => {
      e.stopPropagation();
      const nowExpanded = !this.expandedPasses.has(passName);
      if (nowExpanded) {
        this.expandedPasses.add(passName);
      } else {
        this.expandedPasses.delete(passName);
      }
      paramsPanel.style.display = nowExpanded ? 'block' : 'none';
      expandBtn.style.transform = nowExpanded ? 'rotate(90deg)' : 'rotate(0deg)';
    };

    nameSpan.addEventListener('click', toggleExpand);
    expandBtn.addEventListener('click', toggleExpand);

    passContainer.appendChild(passItem);
    passContainer.appendChild(paramsPanel);

    // Setup drag and drop
    this.setupDragAndDrop(passItem, passContainer, index);

    return passContainer;
  }

  /**
   * Create parameters panel for a pass
   */
  private createParametersPanel(index: number, pipeline: PipelineInterface, distanceParameters?: Set<string>): HTMLDivElement {
    const paramsPanel = document.createElement('div');
    paramsPanel.style.display = 'none';
    paramsPanel.style.padding = '8px';
    paramsPanel.style.background = '#2a2a2a';
    paramsPanel.style.borderTop = '1px solid #444';

    const currentPass = pipeline.getPass(index);
    if (!currentPass) return paramsPanel;

    const kwargs = currentPass.kwargs || {};

    Object.keys(kwargs).forEach((key) => {
      const paramRow = document.createElement('div');
      paramRow.style.marginBottom = '6px';
      paramRow.style.display = 'flex';
      paramRow.style.alignItems = 'center';

      // Check if this is a distance parameter that should be shown in cm
      const isDistanceParam = distanceParameters?.has(key) ?? false;
      const hasConverters = this.pixelsToCm && this.cmToPixels;
      const showInCm = isDistanceParam && hasConverters;

      const label = document.createElement('label');
      label.textContent = key + (showInCm ? ' (cm):' : ':');
      label.style.marginRight = '8px';
      label.style.flex = '1';
      label.style.fontSize = '11px';
      label.style.overflow = 'hidden';
      label.style.textOverflow = 'ellipsis';
      label.style.whiteSpace = 'nowrap';
      paramRow.appendChild(label);

      const valueType = typeof kwargs[key];
      const input = document.createElement('input');

      if (valueType === 'boolean') {
        input.type = 'checkbox';
        input.checked = kwargs[key] as boolean;
        input.style.width = '16px';
        input.style.height = '16px';
        input.addEventListener('change', () => {
          // Handle parameter change internally
          const pass = pipeline.getPass(index);
          if (pass) {
            if (!pass.kwargs) {
              pass.kwargs = {};
            }
            pass.kwargs[key] = input.checked;
          }
          // Notify callbacks
          this.callbacks.onPassParameterChanged?.(index, key, input.checked);
          this.callbacks.onPipelineChanged?.(this.activeTabId || '');
        });

        paramRow.appendChild(input);
        paramsPanel.appendChild(paramRow);
      } else if (valueType === 'number') {
        input.type = 'number';
        input.step = showInCm ? '0.1' : 'any';

        // Values are stored in cm for distance parameters
        // Display the stored value directly (it's already in cm)
        const storedCmValue = kwargs[key] as number;
        input.value = String(storedCmValue);

        input.style.width = '60px';
        input.style.minWidth = '50px';
        input.style.flexShrink = '1';
        input.style.padding = '2px 4px';
        input.style.background = '#1a1a1a';
        input.style.border = '1px solid #444';
        input.style.color = '#fff';
        input.style.fontSize = '11px';

        // Create pixel value display for distance parameters
        // Shows the pixel equivalent of the cm value
        let pixelDisplay: HTMLSpanElement | null = null;
        if (showInCm) {
          pixelDisplay = document.createElement('span');
          pixelDisplay.style.marginLeft = '8px';
          pixelDisplay.style.fontSize = '10px';
          pixelDisplay.style.color = '#888';
          pixelDisplay.style.minWidth = '60px';
          // Convert cm to pixels for display
          if (!this.cmToPixels) throw new Error('cmToPixels converter not set');
          const pixelEquivalent = this.cmToPixels(storedCmValue);
          pixelDisplay.textContent = `(${pixelEquivalent.toFixed(0)}px)`;
        }

        // Handler for value changes - store cm directly, update pixel display
        const handleValueChange = () => {
          const pass = pipeline.getPass(index);
          if (pass) {
            if (!pass.kwargs) {
              pass.kwargs = {};
            }
            const inputValue = parseFloat(input.value);
            if (isNaN(inputValue)) return; // Skip invalid values

            // Store the cm value directly (no conversion needed)
            pass.kwargs[key] = inputValue;

            // Update pixel display to show equivalent
            if (pixelDisplay && showInCm && this.cmToPixels) {
              const pixelEquivalent = this.cmToPixels(inputValue);
              pixelDisplay.textContent = `(${pixelEquivalent.toFixed(0)}px)`;
            }

            // Notify callbacks with the cm value
            this.callbacks.onPassParameterChanged?.(index, key, inputValue);
            this.callbacks.onPipelineChanged?.(this.activeTabId || '');
          }
        };

        // Update pixel display while typing (without saving)
        if (showInCm && pixelDisplay) {
          input.addEventListener('input', () => {
            const inputValue = parseFloat(input.value);
            if (!isNaN(inputValue) && this.cmToPixels) {
              const pixelEquivalent = this.cmToPixels(inputValue);
              pixelDisplay.textContent = `(${pixelEquivalent.toFixed(0)}px)`;
            }
          });
        }

        // Fire on blur (change) and also on Enter key to save
        input.addEventListener('change', handleValueChange);
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            handleValueChange();
            input.blur();
          }
        });

        // Add input and pixel display to row (handled here for number+cm case)
        paramRow.appendChild(input);
        if (pixelDisplay) {
          paramRow.appendChild(pixelDisplay);
        }
        paramsPanel.appendChild(paramRow);
      } else {
        input.type = 'text';
        input.value = String(kwargs[key]);
        input.style.width = '60px';
        input.style.minWidth = '50px';
        input.style.flexShrink = '1';
        input.style.padding = '2px 4px';
        input.style.background = '#1a1a1a';
        input.style.border = '1px solid #444';
        input.style.color = '#fff';
        input.style.fontSize = '11px';
        input.addEventListener('change', () => {
          // Handle parameter change internally
          const pass = pipeline.getPass(index);
          if (pass) {
            if (!pass.kwargs) {
              pass.kwargs = {};
            }
            pass.kwargs[key] = input.value;
          }
          // Notify callbacks
          this.callbacks.onPassParameterChanged?.(index, key, input.value);
          this.callbacks.onPipelineChanged?.(this.activeTabId || '');
        });

        paramRow.appendChild(input);
        paramsPanel.appendChild(paramRow);
      }
    });

    return paramsPanel;
  }

  /**
   * Setup drag and drop for pass reordering
   */
  private setupDragAndDrop(passItem: HTMLDivElement, passContainer: HTMLDivElement, index: number): void {
    passItem.addEventListener('dragstart', (e) => {
      passContainer.style.opacity = '0.5';
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
      }
    });

    passItem.addEventListener('dragend', () => {
      passContainer.style.opacity = '1';
      const allContainers = this.passesContainer.querySelectorAll('[data-index]');
      allContainers.forEach((el) => {
        (el as HTMLElement).style.borderTop = 'none';
        (el as HTMLElement).style.borderBottom = 'none';
      });
    });

    passContainer.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }

      const rect = passContainer.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      const insertBefore = e.clientY < midpoint;

      const allContainers = this.passesContainer.querySelectorAll('[data-index]');
      allContainers.forEach((el) => {
        (el as HTMLElement).style.borderTop = 'none';
        (el as HTMLElement).style.borderBottom = 'none';
      });

      if (insertBefore) {
        passContainer.style.borderTop = '2px solid #2196F3';
      } else {
        passContainer.style.borderBottom = '2px solid #2196F3';
      }

      return false;
    });

    passContainer.addEventListener('dragleave', (e) => {
      const rect = passContainer.getBoundingClientRect();
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        passContainer.style.borderTop = 'none';
        passContainer.style.borderBottom = 'none';
      }
    });

    passContainer.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      passContainer.style.borderTop = 'none';
      passContainer.style.borderBottom = 'none';

      if (!e.dataTransfer) return;
      const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
      const rect = passContainer.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      const insertBefore = e.clientY < midpoint;
      let toIndex = index;

      if (!insertBefore) {
        toIndex = index + 1;
      }

      if (fromIndex < toIndex) {
        toIndex--;
      }

      if (fromIndex !== toIndex) {
        // Handle move internally
        const pipeline = this.getActivePipeline();
        if (pipeline?.movePass(fromIndex, toIndex)) {
          this.refreshPassesList();
        }
        // Notify callbacks
        this.callbacks.onPassMoved?.(fromIndex, toIndex);
        this.callbacks.onPipelineChanged?.(this.activeTabId || '');
      }
      return false;
    });
  }

  /**
   * Create button container
   */
  private createButtonContainer(): HTMLDivElement {
    const buttonContainer = document.createElement('div');
    buttonContainer.style.display = 'flex';
    buttonContainer.style.gap = '5px';
    buttonContainer.style.marginBottom = '10px';
    buttonContainer.style.flexWrap = 'wrap';

    // Save button
    const saveBtn = document.createElement('button');
    saveBtn.textContent = '💾 Save';
    saveBtn.style.flex = '1';
    saveBtn.style.padding = '5px';
    saveBtn.style.cursor = 'pointer';
    saveBtn.style.background = '#4CAF50';
    saveBtn.style.border = 'none';
    saveBtn.style.color = 'white';
    saveBtn.style.borderRadius = '3px';
    saveBtn.onclick = () => this.savePipeline();
    buttonContainer.appendChild(saveBtn);

    // Load button
    const loadBtn = document.createElement('button');
    loadBtn.textContent = '📂 Load';
    loadBtn.style.flex = '1';
    loadBtn.style.padding = '5px';
    loadBtn.style.cursor = 'pointer';
    loadBtn.style.background = '#2196F3';
    loadBtn.style.border = 'none';
    loadBtn.style.color = 'white';
    loadBtn.style.borderRadius = '3px';
    loadBtn.onclick = () => this.loadPipeline();
    buttonContainer.appendChild(loadBtn);

    // Add pass button
    const addBtn = document.createElement('button');
    addBtn.textContent = '➕ Add';
    addBtn.style.flex = '1';
    addBtn.style.padding = '5px';
    addBtn.style.cursor = 'pointer';
    addBtn.style.background = '#FF9800';
    addBtn.style.border = 'none';
    addBtn.style.color = 'white';
    addBtn.style.borderRadius = '3px';
    addBtn.onclick = () => this.showAddPassDialog();
    buttonContainer.appendChild(addBtn);

    return buttonContainer;
  }

  /**
   * Create global toggle section
   */
  private createGlobalToggle(): HTMLDivElement {
    const globalToggle = document.createElement('div');
    globalToggle.style.padding = '8px';
    globalToggle.style.background = 'rgba(255, 255, 255, 0.03)';
    globalToggle.style.borderRadius = '3px';
    globalToggle.style.marginBottom = '8px';
    globalToggle.style.display = 'flex';
    globalToggle.style.alignItems = 'center';

    const globalCheckbox = document.createElement('input');
    globalCheckbox.type = 'checkbox';
    globalCheckbox.checked = true;
    globalCheckbox.style.marginRight = '8px';

    const globalLabel = document.createElement('label');
    globalLabel.textContent = 'Toggle All Passes';
    globalLabel.style.flex = '1';
    globalLabel.style.cursor = 'pointer';
    globalLabel.style.fontWeight = 'bold';
    globalLabel.addEventListener('click', () => globalCheckbox.click());

    globalCheckbox.addEventListener('change', () => {
      const pipeline = this.getActivePipeline();
      if (!pipeline) return;

      const isChecked = globalCheckbox.checked;
      const passNames = pipeline.getPassNames();

      passNames.forEach((passName) => {
        if (isChecked) {
          pipeline.enablePass(passName);
        } else {
          pipeline.disablePass(passName);
        }
        const checkbox = this.passCheckboxes.get(passName);
        if (checkbox) {
          checkbox.checked = isChecked;
        }
      });

      // Notify callbacks
      this.callbacks.onAllPassesToggled?.(isChecked);
      this.callbacks.onPipelineChanged?.(this.activeTabId || '');
    });

    globalToggle.appendChild(globalCheckbox);
    globalToggle.appendChild(globalLabel);

    return globalToggle;
  }

  /**
   * Update checkbox for a specific pass
   */
  updatePassCheckbox(passName: string, checked: boolean): void {
    const checkbox = this.passCheckboxes.get(passName);
    if (checkbox) {
      checkbox.checked = checked;
    }
  }

  /**
   * Save the active pipeline to a JSON file
   */
  savePipeline(): void {
    const pipeline = this.getActivePipeline();
    if (!pipeline) {
      alert('No pipeline selected');
      return;
    }

    const tabId = this.getActiveTabId() || 'unknown';
    const json = pipeline.exportToJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Use the original filename if available, otherwise fall back to tab-based name
    a.download = pipeline.loadedFromFilename || `${tabId}-pipeline-config.json`;
    a.click();
    URL.revokeObjectURL(url);

    this.callbacks.onPipelineChanged?.(tabId);
  }

  /**
   * Load pipeline configuration from a JSON file
   */
  loadPipeline(): void {
    const pipeline = this.getActivePipeline();
    const tabId = this.getActiveTabId() || 'unknown';
    if (!pipeline) {
      alert('No pipeline selected');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const json = e.target?.result as string;

        if (pipeline.loadFromJSON(json)) {
          this.refreshPassesList();
          this.callbacks.onPipelineChanged?.(tabId);
        } else {
          alert('Failed to load pipeline configuration');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  /**
   * Show dialog to add a new pass to the active pipeline
   */
  showAddPassDialog(): void {
    const pipeline = this.getActivePipeline();
    const tabId = this.getActiveTabId() || 'unknown';
    if (!pipeline) {
      alert('No pipeline selected');
      return;
    }

    // Get available processors from registry
    const registry = pipeline.processorRegistry;
    if (!registry) {
      alert('Cannot access processor registry');
      return;
    }

    const availableProcessors = Array.from(registry.keys()) as string[];
    if (availableProcessors.length === 0) {
      alert('No processors available to add');
      return;
    }

    // Create modal dialog
    const dialog = document.createElement('div');
    dialog.style.position = 'fixed';
    dialog.style.top = '50%';
    dialog.style.left = '50%';
    dialog.style.transform = 'translate(-50%, -50%)';
    dialog.style.background = 'rgba(0, 0, 0, 0.9)';
    dialog.style.padding = '20px';
    dialog.style.borderRadius = '5px';
    dialog.style.zIndex = '10000';
    dialog.style.color = 'white';
    dialog.style.fontFamily = 'monospace';
    dialog.style.minWidth = '300px';

    const title = document.createElement('h3');
    title.textContent = 'Add Processing Pass';
    title.style.marginTop = '0';
    dialog.appendChild(title);

    const label = document.createElement('label');
    label.textContent = 'Select pass type:';
    label.style.display = 'block';
    label.style.marginBottom = '10px';
    dialog.appendChild(label);

    const select = document.createElement('select');
    select.style.width = '100%';
    select.style.padding = '5px';
    select.style.marginBottom = '15px';
    select.style.background = '#333';
    select.style.color = 'white';
    select.style.border = '1px solid #555';
    select.style.borderRadius = '3px';
    select.style.fontSize = '14px';
    availableProcessors.forEach((id) => {
      const option = document.createElement('option');
      option.value = id;
      option.textContent = id.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      option.style.background = '#333';
      option.style.color = 'white';
      select.appendChild(option);
    });
    dialog.appendChild(select);

    const btnContainer = document.createElement('div');
    btnContainer.style.display = 'flex';
    btnContainer.style.gap = '10px';

    const addBtn = document.createElement('button');
    addBtn.textContent = 'Add';
    addBtn.style.flex = '1';
    addBtn.style.padding = '8px';
    addBtn.style.cursor = 'pointer';
    addBtn.style.background = '#4CAF50';
    addBtn.style.border = 'none';
    addBtn.style.color = 'white';
    addBtn.style.borderRadius = '3px';
    addBtn.style.fontSize = '14px';
    addBtn.onclick = () => {
      const processorId = select.value;

      // Add the pass to the pipeline using the registry
      if (pipeline.addPassFromRegistry) {
        const success = pipeline.addPassFromRegistry(processorId);
        if (success) {
          this.refreshPassesList();
          this.callbacks.onPipelineChanged?.(tabId);
        } else {
          alert(`Failed to add processor: ${processorId}`);
        }
      } else {
        alert('Pipeline does not support adding passes');
      }

      document.body.removeChild(dialog);
    };
    btnContainer.appendChild(addBtn);

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.flex = '1';
    cancelBtn.style.padding = '8px';
    cancelBtn.style.cursor = 'pointer';
    cancelBtn.style.background = '#f44336';
    cancelBtn.style.border = 'none';
    cancelBtn.style.color = 'white';
    cancelBtn.style.borderRadius = '3px';
    cancelBtn.style.fontSize = '14px';
    cancelBtn.onclick = () => {
      document.body.removeChild(dialog);
    };
    btnContainer.appendChild(cancelBtn);

    dialog.appendChild(btnContainer);
    document.body.appendChild(dialog);
  }
}
